import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import themePacks from '../data/themePacks';
import { CartContext } from '../context/CartContext';

export default function ThemePacks() {
  const [selectedPack, setSelectedPack] = useState(null);
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate();

  const openModal = (pack) => setSelectedPack(pack);
  const closeModal = () => setSelectedPack(null);

  const parsePrice = (p) => {
    if (!p) return 0;
    const num = ('' + p).replace(/[^0-9.]/g, '');
    return Number(num) || 0;
  };

  const addAndCheckout = () => {
    if (!selectedPack) return;
    const item = {
      id: selectedPack.id,
      name: selectedPack.title,
      imgVariants: [selectedPack.img],
      starting_price: parsePrice(selectedPack.price),
      description: selectedPack.description,
    };
    addToCart(item);
    closeModal();
    navigate('/checkout');
  };

  return (
    <>
      {/* Theme based flower packs */}
      <section className="py-12 bg-jasmine-white rounded-3xl border border-[#e7e3cf] mb-12">
        <div className="px-8 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#1b190d]">
                Theme-Based Flower Packs –
                <span className="text-festive-orange">Complete Solutions</span>
              </h2>
              <p className="text-[#9a8d4c] font-medium text-lg mt-2">
                One-click floral arrangements curated for your special moments
              </p>
            </div>
            <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full border border-festive-gold/20">
              <span className="material-symbols-outlined text-festive-orange !text-sm">verified</span>
              <span className="text-xs font-bold uppercase tracking-widest text-festive-gold">Hyperlocal Curated</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {themePacks.map((pack) => (
              <div key={pack.id} className="theme-card rounded-2xl border border-festive-gold/20 shadow-lg overflow-hidden flex flex-col group hover:border-festive-gold transition-all">
                <div className="h-48 relative overflow-hidden">
                  <img alt={pack.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={pack.img} />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full flex items-center gap-1 shadow-sm">
                    <span className="material-symbols-outlined !text-sm text-festive-orange">{pack.tagIcon}</span>
                    <span className="text-[10px] font-bold uppercase tracking-tight">{pack.tagText}</span>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h4 className="text-xl font-black mb-1">{pack.title}</h4>
                  <p className="text-sm text-[#9a8d4c] mb-4">{pack.description}</p>
                  <div className="space-y-2 mb-6">
                    {pack.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] font-semibold text-[#4a4736]">
                        <span className="material-symbols-outlined !text-sm text-festive-gold">check_circle</span>
                        {f}
                      </div>
                    ))}
                  </div>
                  <div className="mt-auto">
                    <div className="bg-festive-orange/5 border border-festive-orange/20 rounded-lg p-3 mb-4">
                      <p className="text-[10px] font-bold text-festive-orange uppercase tracking-wider mb-1">Fixed Price – No Hassle</p>
                      <span className="text-2xl font-black text-[#1b190d]">{pack.price}</span>
                    </div>
                    <button onClick={() => openModal(pack)} className="w-full bg-festive-orange text-white py-3 rounded-xl font-bold hover:bg-[#e65b00] transition-all shadow-md mb-4">Order Now</button>
                    <div className="flex justify-between items-center px-1">
                      <div className="flex flex-col items-center gap-1 group/badge">
                        <span className="material-symbols-outlined !text-lg text-green-600">eco</span>
                        <span className="text-[9px] font-bold text-[#9a8d4c]">Fresh</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="material-symbols-outlined !text-lg text-blue-500">bolt</span>
                        <span className="text-[9px] font-bold text-[#9a8d4c]">Same-Day</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="material-symbols-outlined !text-lg text-amber-500">alarm</span>
                        <span className="text-[9px] font-bold text-[#9a8d4c]">Early Morning</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto border-t border-festive-gold/10 pt-10 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center gap-3 group">
                <div className="size-14 rounded-full bg-white border-2 border-festive-orange text-festive-orange flex items-center justify-center font-black text-xl shadow-sm group-hover:bg-festive-orange group-hover:text-white transition-colors">1</div>
                <p className="font-bold text-sm text-[#1b190d]">Choose Pack</p>
              </div>
              <div className="flex-1 h-px bg-dashed bg-gradient-to-r from-festive-orange to-festive-gold mx-4 mb-8"></div>
              <div className="flex flex-col items-center gap-3 group">
                <div className="size-14 rounded-full bg-white border-2 border-festive-orange text-festive-orange flex items-center justify-center font-black text-xl shadow-sm group-hover:bg-festive-orange group-hover:text-white transition-colors">2</div>
                <p className="font-bold text-sm text-[#1b190d]">Select Date &amp; Time</p>
              </div>
              <div className="flex-1 h-px bg-dashed bg-gradient-to-r from-festive-gold to-festive-orange mx-4 mb-8"></div>
              <div className="flex flex-col items-center gap-3 group">
                <div className="size-14 rounded-full bg-white border-2 border-festive-orange text-festive-orange flex items-center justify-center font-black text-xl shadow-sm group-hover:bg-festive-orange group-hover:text-white transition-colors">3</div>
                <p className="font-bold text-sm text-[#1b190d]">Delivered Fresh</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal for selected pack */}
      {selectedPack && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal}></div>
          <div className="relative bg-white rounded-2xl max-w-2xl w-full p-6 z-10 mx-4">
            <button className="absolute top-4 right-4 text-gray-600 text-2xl leading-none" onClick={closeModal} aria-label="Close">×</button>
            <div className="flex flex-col md:flex-row gap-4">
              <img src={selectedPack.img} alt={selectedPack.title} className="w-full md:w-48 h-40 object-cover rounded-lg" />
              <div className="flex-1">
                <h3 className="text-2xl font-extrabold mb-2">{selectedPack.title}</h3>
                <p className="text-sm text-[#9a8d4c] mb-3">{selectedPack.description}</p>
                <ul className="mb-3 list-disc list-inside text-sm text-[#4a4736]">
                  {selectedPack.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <div className="mb-4">
                  <div className="text-sm text-[#9a8d4c]">Price</div>
                  <div className="text-2xl font-black">{selectedPack.price}</div>
                </div>
                <div className="flex gap-3">
                  <button onClick={addAndCheckout} className="bg-primary text-white px-4 py-2 rounded-lg font-bold">Add to cart & Checkout</button>
                  <button onClick={closeModal} className="px-4 py-2 rounded-lg border">Continue shopping</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
